import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { convertTimeStringToNum } from "@iventis/utilities";
import { findAllEventBlockDescendantsBySpecificRelativeEventRuleRelationship } from "./time-rules.helpers";
import { getEventBlockCode } from "./event-block-helpers";
import { getSubEventDisplayDetails } from "./sub-event-card-content";

// From the parent (session), get all the potential relative event block options
export const getPotentialRelativeEventBlocks = (
    eventBlockHierarchy: Hierarchy<EventBlock>,
    id: string
): { id: string; name: string; type: ScheduleConfigEventBlockType; startAt?: Date; endAt?: Date }[] => {
    const potentials = [eventBlockHierarchy.item as EventBlock, ...(eventBlockHierarchy.children.map((c) => c.item) as EventBlock[])].reduce((cum, item) => {
        if (item.id !== id) {
            return cum.concat([{ id: item.id, name: getEventBlockNameUsingHierarchy(item, eventBlockHierarchy), type: item.type, startAt: item.startAt, endAt: item.endAt }]);
        }
        return cum;
    }, []);

    const relatedEventBlockIds = findAllEventBlockDescendantsBySpecificRelativeEventRuleRelationship(eventBlockHierarchy, id).map((e) => e.id);
    // Only return event blocks that are not currently an descendant by "specific" relative event rule relationship.
    // This is because it any descendants would cause a circular depedency.
    return potentials.filter((option) => !relatedEventBlockIds.includes(option.id));
};

export const getEventBlockNameUsingHierarchy = (eventBlock: EventBlock, eventBlockHierarchy: Hierarchy<EventBlock>) => {
    switch (eventBlock.type.calendarLevel) {
        case 1: // If session
            return getEventBlockCode(eventBlock);
        case 2: // If event unit
            return getSubEventDisplayDetails(eventBlock, eventBlockHierarchy)?.description;
        default:
            return "";
    }
};

// Takes an array of relative rules and checks if all the rules are valid
export const isRelativeRulesValid = (relativeRules: RelativeEventRule[]) =>
    // Cycle through the array of relative rules
    !relativeRules.some((rule) =>
        // Cycle through the relative rule
        Object.keys(rule).some((ruleKey: keyof RelativeEventRule) => {
            // Don't want to test recurring event rule as it can be null
            if (ruleKey === "recurringEventRules") {
                return false;
            }
            // Time values need to check all elements inside the string
            if (ruleKey === "offset" || ruleKey === "duration") {
                // Spilt the time up into three numbers
                const timeNumber = convertTimeStringToNum(rule[ruleKey]);
                // Check that the length is not 3 or if any of the elements are NaN then return not valid
                return timeNumber.length !== 3 || timeNumber.some((timeElement) => Number.isNaN(timeElement));
            }
            // if any rule is equal to null or undefined then return not valid
            return rule[ruleKey] == null;
        })
    );
